import {
  Checkbox,
  Col,
  Icon,
  Input,
  Row,
  Text,
  Tooltip,
} from '@cubeyou-team/hx-react-components';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getItemIcon } from 'src/utils';
import { getFormattedDate, isEmpty } from 'src/utils/misc';
import { StyledItem, StyledText } from './RepositoryItemExtended.styles';
import {
  StyledRepositoryItemStatus,
  StyledRepositoryItemStatusDots,
} from '../RepositoryItem/StyledRepositoryItem';
import { requestRenameItem, requestSaveAudience } from '../../actions';
import ExtendedItemActions from './components/ExtendedItemActions';
import { getCurrentProductDataSources } from '../../selectors/myCompany';

const RepositoryItemExtended = ({
  style,
  checked,
  item,
  navigationDisabled,
  actions,
  onSelect,
  onFolderClick,
}) => {
  const productDataSources = useSelector(state =>
    getCurrentProductDataSources(state)
  );
  const { label, metadata, persistence, type, creation_date } = item;
  const dispatch = useDispatch();
  const [newLabel, setNewLabel] = useState(label);
  const [isEditing, setEditing] = useState(false);
  const icon = getItemIcon(item, productDataSources);
  const repositoryFilter = useSelector(state => state.repository.filter);
  const audiencesToCompare = useSelector(
    state => state.repository.audiencesToCompare
  );
  const isCompared = audiencesToCompare.some(
    audience => audience.id === item.id
  );

  const loaded = typeof children === 'object';
  const status = item.metadata && item.metadata.status;

  const statusLabel = useMemo(() => {
    const mapLabels = {
      ready: 'Ready',
      matching: 'Matching',
      error: 'Error',
    };
    return mapLabels[status] || 'None';
  }, [status]);

  const onSave = useCallback(() => {
    setEditing(false);
    if (label !== newLabel) {
      if (item.persistence === 'permanent') {
        dispatch(requestRenameItem(item.id, newLabel));
      } else {
        dispatch(
          requestSaveAudience(item.id, {
            label: newLabel,
            parent_id: 0,
          })
        );
      }
    }
  }, [dispatch, label, newLabel, item]);

  const handleLabelKeyDown = useCallback(
    event => {
      if (event.keyCode === 13) {
        onSave();
      }
    },
    [onSave]
  );

  const labelText = useMemo(() => {
    if (item.type === 'folder') {
      return (
        <Text
          size="small"
          title={item.label}
          style={{
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          {item.label}
        </Text>
      );
    }
    return (
      <React.Fragment>
        {!isEditing ? (
          <StyledText
            size="small"
            title={newLabel}
            onDoubleClick={e => {
              e.persist();
              setEditing(true);
            }}
          >
            {newLabel}
          </StyledText>
        ) : (
          <Input
            type="text"
            name="tab-edit"
            size="thin"
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            onKeyDown={e => handleLabelKeyDown(e)}
            onBlur={onSave}
            autoFocus
          />
        )}
      </React.Fragment>
    );
  }, [item, isEditing, newLabel, onSave, handleLabelKeyDown]);

  return (
    <StyledItem style={style} isTemporary={persistence === 'temporary'}>
      <div style={{ width: '50px' }}>
        <Checkbox
          checked={checked}
          disabled={
            !isEmpty(audiencesToCompare) ||
            (metadata &&
              ['conversation', 'tsv'].includes(metadata.audience_type) &&
              metadata.status !== 'ready')
          }
          onChange={() => {
            onSelect(item.id);
          }}
        />
      </div>
      <div
        style={{ width: 'calc(100% - 50px)' }}
        onClick={() => {
          if (type === 'folder' && !navigationDisabled) {
            onFolderClick(item, loaded);
          }
        }}
      >
        <Row style={{ width: '100%' }}>
          <Col span={3} style={{ display: 'flex', alignItems: 'center' }}>
            <Icon
              style={{ paddingRight: '10px' }}
              size="small"
              icon={icon}
              variant="dark"
            />
            {labelText}
          </Col>
          <Col
            span={['conversation', 'tsv'].includes(repositoryFilter) ? 2 : 3}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Text size="small">
              {creation_date &&
                getFormattedDate(creation_date * 1000, 'MM/dd/yyyy')}
            </Text>
          </Col>
          {metadata &&
            persistence === 'temporary' &&
            ['conversation', 'tsv'].includes(metadata.audience_type) &&
            metadata.status !== 'matching' && (
              <Col span={2}>
                <Tooltip
                  content={statusLabel}
                  maxWidth="small"
                  position="top"
                  isInfoLabel
                >
                  <StyledRepositoryItemStatus status={metadata.status} />
                </Tooltip>
              </Col>
            )}
          {metadata &&
            persistence === 'temporary' &&
            ['conversation', 'tsv'].includes(metadata.audience_type) &&
            metadata.status === 'matching' && (
              <Col span={2} style={{ display: 'flex', alignItems: 'center' }}>
                <StyledRepositoryItemStatusDots>
                  <div className="dots" />
                </StyledRepositoryItemStatusDots>
              </Col>
            )}
          {metadata &&
            persistence !== 'temporary' &&
            ['conversation', 'tsv'].includes(metadata.audience_type) && (
              <Col span={2} style={{ display: 'flex', alignItems: 'center' }} />
            )}
          {isCompared && (
            <Col span="auto" style={{ display: 'flex', alignItems: 'center' }}>
              <Icon size="small" icon="done" variant="green" />
              <Text
                weight="semi-bold"
                variant="green"
                style={{ paddingLeft: '10px' }}
              >
                COMPARED
              </Text>
            </Col>
          )}
        </Row>
      </div>
      <ExtendedItemActions item={item} actions={actions} />
    </StyledItem>
  );
};
export default RepositoryItemExtended;
